import React, { InputHTMLAttributes, forwardRef, useState, useId } from 'react';
import { AlertCircle, CheckCircle } from 'lucide-react';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  success?: string;
  hint?: string;
  /** Size variant */
  inputSize?: 'sm' | 'md' | 'lg';
  /** Left icon or element */
  leftElement?: React.ReactNode;
  /** Right icon or element */
  rightElement?: React.ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      success,
      hint,
      inputSize = 'md',
      leftElement,
      rightElement,
      className = '',
      id,
      onFocus,
      onBlur,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = id || generatedId;
    const [isFocused, setIsFocused] = useState(false);

    const sizeStyles = {
      sm: 'px-3 py-2 text-sm min-h-[36px]',
      md: 'px-4 py-3 text-base min-h-[44px]',
      lg: 'px-5 py-4 text-lg min-h-[52px]',
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      onBlur?.(e);
    };

    // Determine border color based on state
    const getBorderColor = () => {
      if (error) return 'border-red-500 focus:border-red-500';
      if (success) return 'border-green-500 focus:border-green-500';
      return 'border-slate-700 focus:border-st-primary';
    };

    // Determine ring color based on state
    // Using ring-2 with full color for better visibility (WCAG 2.1 AA compliance)
    const getRingColor = () => {
      if (error) return 'focus:ring-red-500';
      if (success) return 'focus:ring-green-500';
      return 'focus:ring-st-primary';
    };

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className={`block text-sm font-bold mb-2 transition-colors duration-200 ${
              isFocused ? 'text-st-primary' : 'text-slate-300'
            }`}
          >
            {label}
          </label>
        )}

        <div className="relative">
          {/* Left element - using slate-400 for better contrast */}
          {leftElement && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
              {leftElement}
            </div>
          )}

          <input
            ref={ref}
            id={inputId}
            className={[
              'w-full bg-st-slate rounded text-white placeholder-slate-400',
              // Smooth transitions
              'transition-all duration-200 ease-out',
              // Border styling
              'border',
              getBorderColor(),
              // Focus ring - using ring-2 for consistent, visible focus indicator
              'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-st-dark',
              getRingColor(),
              // Size styling
              sizeStyles[inputSize],
              // Padding adjustments for elements
              leftElement ? 'pl-10' : '',
              rightElement || error || success ? 'pr-10' : '',
              // Disabled state
              'disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-slate-800',
              className,
            ].join(' ')}
            onFocus={handleFocus}
            onBlur={handleBlur}
            aria-invalid={error ? 'true' : undefined}
            aria-describedby={
              error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined
            }
            {...props}
          />

          {/* Right element or status icon */}
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            {rightElement ? (
              rightElement
            ) : error ? (
              <AlertCircle className="w-5 h-5 text-red-500 animate-pulse" />
            ) : success ? (
              <CheckCircle className="w-5 h-5 text-green-500" />
            ) : null}
          </div>
        </div>

        {/* Error message with animation */}
        {error && (
          <p
            id={`${inputId}-error`}
            className="mt-2 text-sm text-red-400 flex items-center gap-1.5 animate-in slide-in-from-top-1 duration-200"
            role="alert"
          >
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </p>
        )}

        {/* Success message */}
        {success && !error && (
          <p className="mt-2 text-sm text-green-400 flex items-center gap-1.5">
            <CheckCircle className="w-4 h-4 flex-shrink-0" />
            {success}
          </p>
        )}

        {/* Hint text - using slate-400 for AA compliance (5.7:1 on dark bg) */}
        {hint && !error && !success && (
          <p id={`${inputId}-hint`} className="mt-2 text-sm text-slate-400">
            {hint}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

